import { Link } from 'react-router-dom';
import { useScrollAnimations } from '../hooks/useScrollAnimation';
import SEO from '../components/SEO';

const VALUES = [
  {
    title: 'Operational clarity first',
    description:
      'We look at how a business actually operates before recommending anything. The right solution depends entirely on understanding the specific situation.',
  },
  {
    title: 'No unnecessary complexity',
    description:
      'Simple, well-designed processes outperform complicated ones. We add only what is needed and nothing more.',
  },
  {
    title: 'The customer experience is the standard',
    description:
      'Everything we build is evaluated through the lens of what a customer encounters. If it creates friction, it needs to be reconsidered.',
  },
  {
    title: 'Improvement is ongoing',
    description:
      'A business that works well today may need to change tomorrow. We design for adaptability and stay engaged beyond the initial build.',
  },
];

export default function About() {
  const containerRef = useScrollAnimations();

  return (
    <div ref={containerRef}>
      <SEO 
        title="About Us | 7th Vision" 
        description="Learn about 7th Vision's approach to business operations, communication, and web design. We look at how a business actually operates before recommending anything."
      />
      {/* Hero */}
      <section className="relative min-h-[55vh] sm:min-h-[65vh] bg-stone-800 flex flex-col justify-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-800/20 to-stone-800" />
        <div className="relative section-padding pb-12 sm:pb-20 pt-24 sm:pt-40">
          <p className="section-label text-bronze-400 mb-4">About</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-100 leading-tight max-w-3xl mb-6">
            We take business operations seriously.
          </h1>
          <p className="text-stone-400 text-lg font-light max-w-xl leading-relaxed">
            Because the details of how a business runs, how it communicates, how it follows
            up, how it presents itself, determine what customers actually experience.
          </p>
        </div>
      </section>

      {/* Core Message */}
      <section className="section-padding py-14 md:py-32 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-start">
            <div className="fade-up">
              <p className="section-label mb-4">What We Believe</p>
              <h2 className="font-serif text-3xl md:text-4xl text-stone-800 leading-tight mb-8">
                Most business problems are operational problems in disguise.
              </h2>
              <div className="w-12 h-px bg-bronze-400 mb-8" />
              <div className="space-y-5 text-stone-600 leading-relaxed font-light">
                <p>
                  A business can have an excellent service, a skilled team, and satisfied
                  customers, and still lose opportunities regularly because of how it handles
                  communication, how its website presents information, or how its internal
                  processes are structured.
                </p>
                <p>
                  These are not failures of vision or effort. They are gaps in the day-to-day
                  operations that support the business, gaps that are often invisible until they become
                  significant.
                </p>
                <p>
                  Our work is to find those gaps, address them thoughtfully, and establish clear
                  processes that allow the business to operate with greater reliability and a better
                  customer experience.
                </p>
              </div>
            </div>

            <div className="fade-in">
              <img
                src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Business professionals in conversation"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section-padding py-14 md:py-32 bg-stone-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 md:mb-16 fade-up">
            <p className="section-label text-bronze-400 mb-4">How We Work</p>
            <h2 className="font-serif text-3xl md:text-4xl text-stone-100 leading-tight max-w-2xl">
              Every engagement begins with a real understanding of the business.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-700">
            {[
              {
                heading: 'We listen before we recommend.',
                body: 'We take the time to understand how your business currently operates: the workflows, the communication patterns, the friction points, before suggesting any changes.',
              },
              {
                heading: 'We are direct and clear.',
                body: 'If something will not make a meaningful difference, we say so. Our goal is to earn long-term trust by giving honest assessments and realistic expectations.',
              },
              {
                heading: 'We design for durability.',
                body: 'The solutions we put in place are designed to keep working reliably over time, without requiring constant maintenance or specialized support to manage.',
              },
              {
                heading: 'We stay involved.',
                body: 'We do not hand over a finished project and disappear. We monitor what we build, refine it as we learn more, and remain available as the business evolves.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-stone-900 p-6 md:p-10 fade-up"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-8 h-px bg-bronze-500 mb-6" />
                <h3 className="font-serif text-xl text-stone-200 mb-4">{item.heading}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding py-14 md:py-32 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 md:mb-16 fade-up">
            <p className="section-label mb-4">Our Standards</p>
            <h2 className="font-serif text-3xl md:text-4xl text-stone-800 leading-tight max-w-xl">
              What guides every decision we make on behalf of a client.
            </h2>
          </div>

          <div className="space-y-px bg-stone-200">
            {VALUES.map((v, i) => (
              <div
                key={v.title}
                className="bg-stone-50 grid grid-cols-1 md:grid-cols-3 gap-6 p-6 md:p-10 fade-up"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div>
                  <span className="font-serif text-4xl text-stone-200 leading-none">
                    0{i + 1}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-serif text-xl text-stone-800 mb-3">{v.title}</h3>
                  <p className="text-stone-500 leading-relaxed text-sm">{v.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="section-padding py-10 md:py-20 bg-stone-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=600',
              'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=600',
              'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=600',
              'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=600',
            ].map((src, i) => (
              <div key={i} className="overflow-hidden fade-in" style={{ transitionDelay: `${i * 100}ms` }}>
                <img
                  src={src}
                  alt="Professional environment"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding py-14 md:py-28 bg-stone-800">
        <div className="max-w-2xl mx-auto text-center fade-up">
          <h2 className="font-serif text-4xl text-stone-100 leading-tight mb-8">
            If your business could run more smoothly, we would like to understand how.
          </h2>
          <div className="w-12 h-px bg-bronze-500 mx-auto mb-10" />
          <p className="text-stone-400 leading-relaxed mb-12 font-light">
            We start every relationship with a conversation. No assumptions, no generic
            recommendations. Just a focused discussion about your specific situation.
          </p>
          <Link to="/contact" className="btn-outline-light">
            Schedule a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
}
